import { useState, useCallback, useContext } from "react"
import { RouteFormData, Route } from "../types/route"
import useCancelToken from "hooks/useCancelToken"
import AuthContext from "store/context/PublicContext"
import { AppContext } from "store/context/AppContext"
import { post_middleware_osrm_search, put_middleware_olm } from "services/custom_services"

const cleanLocationData = (data: any) => {
  if (data && typeof data === 'object') {
    const { custom, id, ownerId, ...cleanData } = data;
    return cleanData;
  }
  return data;
};

// Helper function to clean route data before sending
const cleanRouteData = (routeData: any) => {
  const cleanedData = { ...routeData };
  
  // Clean source data if it's a location
  if (cleanedData.source && cleanedData.source.type === 'Location') {
    cleanedData.source.data = cleanLocationData(cleanedData.source.data);
  }
  
  // Clean target data if it's a location
  if (cleanedData.target && cleanedData.target.type === 'Location') {
    cleanedData.target.data = cleanLocationData(cleanedData.target.data);
  }
  
  return cleanedData;
};

interface UseRouteFormReturn {
  loading: boolean
  error: string | null
  saveRoute: (routeData: RouteFormData) => Promise<boolean>
  loadRoute: (routeId: string) => Promise<Route | null>
}

export const useRouteForm = (): UseRouteFormReturn => {
  const [error, setError] = useState<string | null>(null)
  const { newCancelToken } = useCancelToken()
  const {
    user: { token },
  } = useContext(AuthContext)
  const { loading_app: loading, set_loading_app: setLoading } = useContext(AppContext)

  const saveRoute = useCallback(async (routeData: RouteFormData): Promise<boolean> => {
    try {
      setLoading(true)
      setError(null)

      const cleanedRouteData = cleanRouteData(routeData);

      const data: any = await put_middleware_olm("routes", cleanedRouteData, token, newCancelToken())

      console.log("saveRoute data", data)
      if (data?.data?.code === 200) {
        return true
      } else {
        throw new Error(data.message || "Failed to save route")
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred")
      return false
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadRoute = useCallback(async (routeId: string): Promise<Route | null> => {
    try {
      setLoading(true)
      setError(null)

      // const data = await apiService.searchRoutes({
      const data: any = await post_middleware_osrm_search(
        "routes",
        {
          from: 0,
          size: 1,
          filters: { id: routeId },
        },
        token,
        newCancelToken(),
      )

      if (data?.data?.message?.records?.length > 0) {
        return data?.data?.message?.records[0]
      } else {
        throw new Error("Route not found")
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred")
      return null
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    loading,
    error,
    saveRoute,
    loadRoute,
  }
}
